import React from 'react';
import { whyUs, whyUsImage, processSteps } from '../mock';
import { Check, Binoculars, FileText, Calculator, Wrench, ShieldCheck } from 'lucide-react';

const iconMap = { Binoculars, FileText, Calculator, Wrench, ShieldCheck };

const WhyUsAndProcess = () => {
  return (
    <section className="bg-[#0b0b0b]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-10">
        {/* WHY US */}
        <div className="relative overflow-hidden min-h-[340px] bg-[#0d2818]">
          <div className="absolute inset-0">
            <img src={whyUsImage} alt="Dlaczego LSTOIT" className="w-full h-full object-cover opacity-25 mix-blend-luminosity" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d2818] via-[#0d2818]/85 to-[#0d2818]/40" />
            <div className="absolute inset-0 bg-[#10b97a]/10" />
          </div>
          <div className="relative p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-7">
              <span className="block w-7 h-[2px] bg-[#10b97a]" />
              <h3 className="font-bold tracking-[0.22em] text-[13px] text-white">DLACZEGO LSTOIT?</h3>
            </div>
            <ul className="space-y-4">
              {whyUs.map((w) => (
                <li key={w} className="flex items-start gap-3 text-[13.5px] text-white/90">
                  <Check size={16} className="text-[#10b97a] mt-0.5 shrink-0" strokeWidth={3} />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* PROCESS - LUXURY GREEN */}
        <div className="relative overflow-hidden p-8 lg:p-10 bg-[#06140d]">
          {/* Layered luxury background */}
          <div className="absolute inset-0">
            {/* Base deep emerald with subtle warm undertone */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0f2a1c] via-[#082015] to-[#03100a]" />
            {/* Soft gold radial glow top-left */}
            <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-[#d4af37]/18 blur-3xl" />
            {/* Subtle gold glow bottom-right */}
            <div className="absolute -bottom-32 -right-24 w-[460px] h-[460px] rounded-full bg-[#b8860b]/12 blur-3xl" />
            {/* Emerald glow center for depth */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[320px] rounded-full bg-[#1f6f4a]/15 blur-3xl" />
            {/* Fine grain / noise texture via SVG */}
            <div
              className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
              }}
            />
            {/* Hairline gold top border */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/70 to-transparent" />
            {/* Hairline gold bottom border */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/50 to-transparent" />
          </div>

          <div className="relative">
            <div className="flex items-center gap-3 mb-9">
              <span className="block w-7 h-[2px] bg-gradient-to-r from-[#d4af37] to-[#f4e5a1]" />
              <h3 className="font-bold tracking-[0.28em] text-[12px] text-transparent bg-clip-text bg-gradient-to-r from-[#f4e5a1] via-[#d4af37] to-[#a8842a]">
                PROCES WSPÓŁPRACY
              </h3>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {processSteps.map((s) => {
                const Icon = iconMap[s.icon];
                return (
                  <div key={s.num} className="flex flex-col items-center text-center group">
                    {/* Gold ring + dark green center step number */}
                    <div className="relative">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#f4e5a1] via-[#d4af37] to-[#8a6914] blur-[6px] opacity-50 group-hover:opacity-80 transition-opacity duration-300" />
                      <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#f4e5a1] via-[#d4af37] to-[#8a6914] p-[1.5px] shadow-[0_8px_24px_-6px_rgba(212,175,55,0.5)] group-hover:scale-110 transition-transform duration-300">
                        <div className="w-full h-full rounded-full bg-[#082015] flex items-center justify-center">
                          <span className="text-[13px] font-bold tracking-wide text-transparent bg-clip-text bg-gradient-to-br from-[#f4e5a1] to-[#d4af37]">
                            {s.num}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 text-[#d4af37] group-hover:text-[#f4e5a1] transition-colors duration-200">
                      <Icon size={28} strokeWidth={1.3} />
                    </div>
                    <h4 className="mt-3 text-[12.5px] font-semibold tracking-wide text-white">{s.title}</h4>
                    <p className="mt-1.5 text-[10.5px] leading-relaxed text-white/60 px-1">{s.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUsAndProcess;
